import { computed, type ComputedRef } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { useSwiftaidAuthorisationStatus } from '@/composables/useSwiftaidAuthorisationStatus'
import { useDashboardWelcome } from '@/composables/useDashboardWelcome'

export type UseDashboardTopbarReturn = {
  title: ComputedRef<string>
  subtitle: ComputedRef<string>
  showSubtitle: ComputedRef<boolean>
  showAuthorisationStatusChip: ComputedRef<boolean>
  isAuthorised: ComputedRef<boolean>
  isAuthorisationStatusKnown: ComputedRef<boolean>
  authorisationStatusLabel: ComputedRef<string>
  authorisationChipAriaLabel: ComputedRef<string>
}

function firstNameFromDonor(store: ReturnType<typeof useStore>): string {
  const donor = (
    store.state as {
      donor?: {
        userDetails?: {
          details?: { firstname?: string }
          emailAddress?: string
        }
      }
    }
  ).donor
  const details = donor?.userDetails?.details
  const first = details?.firstname?.trim()
  if (first) return first
  const email = donor?.userDetails?.emailAddress
  if (email?.includes('@')) return email.split('@')[0]
  return ''
}

/**
 * Route-aware headings for the dashboard top bar (BobV1 shell).
 */
export function useDashboardTopbar(): UseDashboardTopbarReturn {
  const route = useRoute()
  const store = useStore()
  const { t } = useI18n()
  const { isAuthorised, isStatusKnown: isAuthorisationStatusKnown } =
    useSwiftaidAuthorisationStatus()
  const { isWelcomeReady, isFirstDashboardVisit, showPaidDashboardSubtitle } =
    useDashboardWelcome()

  const title = computed(() => {
    const nav = route.meta.dashboardNav as string | undefined

    switch (nav) {
      case 'dashboard':
        if (!isWelcomeReady.value) {
          return t('Dashboard.Shell.Loading')
        }
        const greetingName =
          firstNameFromDonor(store) || t('Dashboard.Shell.Topbar.GuestName')
        return isFirstDashboardVisit.value
          ? t('Dashboard.Shell.Topbar.Dashboard.TitleWelcome', {
              name: greetingName,
            })
          : t('Dashboard.Shell.Topbar.Dashboard.TitleWelcomeBack', {
              name: greetingName,
            })
      case 'statements':
        return t('Dashboard.Shell.Topbar.Statements.Title')
      case 'giving':
      case 'donations':
        return t('Dashboard.Shell.Topbar.Giving.Title')
      case 'claim-tax-back':
        return t('Dashboard.Shell.Topbar.ClaimTaxBack.Title')
      case 'account':
        return t('Dashboard.Shell.Topbar.Account.Title')
      case 'support':
        return t('Dashboard.Shell.Topbar.Support.Title')
      default:
        return t('Dashboard.Shell.Topbar.Default.Title')
    }
  })

  const subtitle = computed(() => {
    const nav = route.meta.dashboardNav as string | undefined

    switch (nav) {
      case 'dashboard':
        return showPaidDashboardSubtitle.value
          ? t('Dashboard.Shell.Topbar.Dashboard.SubtitlePaid')
          : ''
      case 'statements':
        return t('Dashboard.Shell.Topbar.Statements.Subtitle')
      case 'giving':
      case 'donations':
        return t('Dashboard.Shell.Topbar.Giving.Subtitle')
      case 'claim-tax-back':
        return t('Dashboard.Shell.Topbar.ClaimTaxBack.Subtitle')
      case 'account':
        return t('Dashboard.Shell.Topbar.Account.Subtitle')
      case 'support':
        return t('Dashboard.Shell.Topbar.Support.Subtitle')
      default:
        return t('Dashboard.Shell.Topbar.Default.Subtitle')
    }
  })

  const showSubtitle = computed(() => subtitle.value.trim().length > 0)

  // Hide the chip until GET /me/authorisation has settled — otherwise a stale
  // “Inactive” label flashes while the real status is still loading.
  const showAuthorisationStatusChip = computed(
    () =>
      isAuthorisationStatusKnown.value &&
      (route.meta.dashboardNav as string | undefined) === 'dashboard'
  )

  const authorisationStatusLabel = computed(() =>
    isAuthorised.value
      ? t('Dashboard.Shell.Topbar.AuthorisationChip.Active')
      : t('Dashboard.Shell.Topbar.AuthorisationChip.Inactive')
  )

  const authorisationChipAriaLabel = computed(() =>
    isAuthorised.value
      ? t('Dashboard.Shell.Topbar.AuthorisationChip.AriaActive')
      : t('Dashboard.Shell.Topbar.AuthorisationChip.AriaInactive')
  )

  return {
    title,
    subtitle,
    showSubtitle,
    showAuthorisationStatusChip,
    isAuthorised,
    isAuthorisationStatusKnown,
    authorisationStatusLabel,
    authorisationChipAriaLabel,
  }
}
